import { z } from 'zod';

import { getConfirmPasswordRefiner } from '../../common/refiners/confirm-password.refiner.ts';
import {
  CodeSchema,
  EmailSchema,
  IdSchema,
  NameSchema,
  PasswordSchema,
} from '../../common/types/common-validation.types.ts';
import { getLocaleUtil } from '../../main/utils/locale.util.ts';

import {
  DEFAULT_PROFILE_SETTINGS,
  MIN_INCORRECT_HELP_THRESHOLD,
  MAX_INCORRECT_HELP_THRESHOLD,
} from '../constants/profile-settings.ts';

export const SignUpInputSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  preferredName: NameSchema,
});

export type SignUpInputType = z.infer<typeof SignUpInputSchema>;

export const SignInInputSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
});

export type SignInInputType = z.infer<typeof SignInInputSchema>;

export const ResetPasswordInputSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  code: CodeSchema,
});

export type ResetPasswordInputType = z.infer<typeof ResetPasswordInputSchema>;

export const ProfileNameSchema = z
  .string()
  .trim()
  .min(1, { message: getLocaleUtil().select('accounts:field_child_name_required') })
  .max(50, { message: getLocaleUtil().select('accounts:field_child_name_max_length') });

export const ProfileAgeSchema = z
  .number()
  .min(-1, { message: getLocaleUtil().select('accounts:field_child_age_min') })
  .max(12, { message: getLocaleUtil().select('accounts:field_child_age_max') });

export const ProfileIncorrectHelpThresholdSchema = z
  .number()
  .min(MIN_INCORRECT_HELP_THRESHOLD, {
    message: getLocaleUtil().select('accounts:field_child_incorrect_help_threshold_min'),
  })
  .max(MAX_INCORRECT_HELP_THRESHOLD, {
    message: getLocaleUtil().select('accounts:field_child_incorrect_help_threshold_max'),
  })
  .default(DEFAULT_PROFILE_SETTINGS.incorrectHelpThreshold);

export const ProfileCreationFormSchema = z.object({
  name: ProfileNameSchema,
  age: ProfileAgeSchema,
  incorrectHelpThreshold: ProfileIncorrectHelpThresholdSchema,
});

export type ProfileCreationFormType = z.infer<typeof ProfileCreationFormSchema>;

export const ProfileUpdateFormSchema = z.object({
  id: IdSchema,
  name: ProfileNameSchema.optional(),
  age: ProfileAgeSchema.optional(),
  incorrectHelpThreshold: ProfileIncorrectHelpThresholdSchema.optional(),
});

export type ProfileUpdateFormType = z.infer<typeof ProfileCreationFormSchema>;

export const ParentAccountUpdateFormSchema = z.object({
  preferredName: NameSchema,
});

export type ParentAccountUpdateFormType = z.infer<typeof ParentAccountUpdateFormSchema>;

export const ChangePasswordFormSchema = z
  .object({
    currentPassword: PasswordSchema,
    newPassword: PasswordSchema,
    confirmPassword: PasswordSchema,
  })
  .refine(...getConfirmPasswordRefiner());

export type ChangePasswordFormType = z.infer<typeof ChangePasswordFormSchema>;
